/**
 * Everything waiting on a person, in the caller's entities.
 *
 * 1. Approval queue: every checkpoint waiting, from `GET /api/approvals`.
 *    Each row is the same card the job screen uses, so approve and deny
 *    behave identically in both places, including the confirmation
 *    before approving an irreversible phase. For that reason there are
 *    no single-key approve shortcuts here.
 *
 * 2. Jobs that need a person at the browser: a job that asked for help
 *    (a CAPTCHA, a page it does not recognise) or one a supervisor is
 *    controlling, from `GET /api/jobs`. These are not decided here —
 *    the row opens the job screen, where the live view and Take control
 *    are. If that list cannot be loaded the section is left out; the
 *    dashboard's attention rail shows the same jobs.
 *
 * Loading / empty / error / permission-denied per Rule Zero #8.
 */
import { Link } from "react-router-dom";

import { StatusPill } from "@/components/ui/StatusPill";
import { WaveMark } from "@/components/ui/WaveMark";
import { ApprovalCard } from "@/features/job/ApprovalPanel";
import { DeniedNotice, ErrorNotice, Skeleton } from "@/features/job/parts";
import { isStatus } from "@/features/job/ui";
import { useApprovalsApi } from "@/lib/hooks/useApprovalsApi";
import { useJobsApi } from "@/lib/hooks/useJobsApi";
import { jobStatusLabel } from "@/lib/jobStatus";

export function ApprovalsScreen() {
  const query = useApprovalsApi();
  const items = query.data?.items ?? [];
  const jobs = useJobsApi();
  // UI bucket `takeover`: waiting for a person, or a supervisor in control.
  const needsPerson = (jobs.data?.items ?? []).filter((job) => job.status === "takeover");

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex items-baseline gap-3 border-b border-paper-line bg-paper-0 px-6 py-4">
        <h1 className="text-lg font-medium text-ink-1">Approvals</h1>
        {query.data && (
          <span className="font-mono text-xs text-ink-4">{items.length} waiting</span>
        )}
        <span className="ml-auto text-xs text-ink-4">Refreshes every 5s</span>
      </header>

      <main className="flex-1 overflow-auto p-6">
        <div className="mx-auto max-w-2xl">
          {needsPerson.length > 0 && (
            <section aria-label="Jobs that need a person" className="mb-8">
              <h2 className="mb-2 text-sm font-medium text-ink-1">
                Jobs that need a person{" "}
                <span className="font-mono text-xs text-ink-4">{needsPerson.length}</span>
              </h2>
              <p className="mb-3 text-xs text-ink-3">
                These jobs stopped because something on the page needs a person, such as a
                CAPTCHA. Open the job to watch its browser and take control.
              </p>
              <ul className="space-y-2">
                {needsPerson.map((job) => (
                  <li
                    key={job.id}
                    className="flex items-center gap-3 rounded-md border border-paper-line bg-paper-2 p-3 text-sm"
                  >
                    <StatusPill
                      status={job.status}
                      label={jobStatusLabel(job.status, job.apiStatus)}
                    />
                    <span className="min-w-0 flex-1 truncate text-ink-1">{job.workflow.name}</span>
                    <span className="text-xs uppercase tracking-wider text-ink-3">
                      {job.entity.id}
                    </span>
                    <Link to={`/jobs/${job.id}`} className="text-xs text-teal-700 underline">
                      Open job
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {query.isPending ? (
            <div role="status" aria-busy="true" className="space-y-4">
              <span className="sr-only">Loading approvals</span>
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          ) : isStatus(query.error, 403) ? (
            <div className="rounded-md border border-paper-line bg-paper-2 p-6">
              <DeniedNotice what="approvals" roles="operator, supervisor or entity admin" />
            </div>
          ) : query.isError && !query.data ? (
            <div className="rounded-md border border-paper-line bg-paper-2 p-6">
              <ErrorNotice
                message="Approvals could not be loaded."
                error={query.error}
                onRetry={() => query.refetch()}
              />
            </div>
          ) : items.length === 0 ? (
            needsPerson.length > 0 ? (
              <p className="text-sm text-ink-3">No approvals waiting.</p>
            ) : (
              <div className="mt-16 text-center text-ink-3">
                <WaveMark size={48} className="mx-auto" />
                <p className="mt-4 text-md text-ink-1">Inbox zero.</p>
                <p className="mt-1 text-sm">No approvals waiting.</p>
              </div>
            )
          ) : (
            <ul className="space-y-6">
              {items.map((a) => (
                <li key={a.id}>
                  <div className="mb-2 flex items-baseline gap-2 text-sm">
                    <span className="font-medium text-ink-1">{a.workflow.name}</span>
                    <span className="text-xs uppercase tracking-wider text-ink-3">
                      {a.entity.id}
                    </span>
                    <Link
                      to={`/jobs/${a.jobId}`}
                      className="ml-auto text-xs text-teal-700 underline"
                    >
                      Open job
                    </Link>
                  </div>
                  <ApprovalCard approval={a} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
